import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faDownload,
  faPaperPlane,
  faShieldHalved,
  faClipboardCheck,
  faCircleCheck,
  faFileLines,
  faLeaf,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import DownloadDataModal from './DownloadDataModal';
import RequestSupplierDataModal from './RequestSupplierDataModal';

// Backend sends each status label as { key, label, tone } — `key` is a stable
// identifier we map to an icon here (the label copy itself is render-as-sent).
// Unknown keys simply render without an icon.
const LABEL_KEY_ICONS = {
  preverification: faShieldHalved,
  'monitoring-plan': faClipboardCheck,
  verified: faCircleCheck,
  'emissions-report': faFileLines,
  'low-carbon': faLeaf,
};

const formatUpdatedAt = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// tone → { bg, text, border } class combo for the pill badge.
const BADGE_TONES = {
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  primary:
    'bg-[color:rgb(var(--primary-color)/0.1)] text-[color:rgb(var(--primary-color-dark))] border-[color:rgb(var(--primary-color)/0.3)]',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
};

const SupplierLabelBadge = ({ labelKey, label, tone = 'neutral' }) => {
  const cls = BADGE_TONES[tone] || BADGE_TONES.neutral;
  const FaIcon = labelKey && LABEL_KEY_ICONS[labelKey];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${cls}`}
    >
      {FaIcon && (
        <FontAwesomeIcon
          icon={FaIcon}
          className="text-[10px]"
        />
      )}
      {label}
    </span>
  );
};

/**
 * The trust + sharing surface at the top of the public share page.
 *
 *   1. Identity / trust   → big logo + name, eyebrow, last-updated stamp,
 *                           coverage badges, and any supplier status labels
 *                           ("In preverification", "Monitoring plan generated").
 *   2. Sharing actions    → two separate buttons, one intent each:
 *                           "Get suppliers CBAM-ready" opens the invite-email
 *                           pitch (RequestSupplierDataModal), "Download data"
 *                           opens the Excel/PDF picker (DownloadDataModal).
 *   3. Carbon Glance pitch → handled by the invite modal above and the
 *                           CbamCostEstimator below the table.
 */
export default function PublicCatalogHeader({ operator, onDownloadExcel, reportPdf }) {
  const updated = formatUpdatedAt(operator?.lastUpdatedAt);
  const labels = Array.isArray(operator?.labels) ? operator.labels : [];
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  return (
    <div className="mx-4 mb-4 rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      <div
        className="h-1 w-full"
        style={{ backgroundColor: 'rgb(var(--primary-color))' }}
      />

      <div className="px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          {/* Identity */}
          <div className="flex items-center gap-3 min-w-0">
            {operator?.logoUrl ? (
              <img
                src={operator.logoUrl}
                alt={operator?.name || 'Operator'}
                className="w-12 h-12 rounded-lg object-cover border border-slate-200 bg-white shrink-0"
              />
            ) : (
              <div
                className="w-12 h-12 rounded-lg border border-slate-200 flex items-center justify-center text-base font-semibold shrink-0"
                style={{
                  backgroundColor: 'rgb(var(--primary-color) / 0.08)',
                  color: 'rgb(var(--primary-color-dark))',
                }}
              >
                {(operator?.name || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                CBAM portal
              </div>
              <h1 className="text-xl font-bold text-slate-900 leading-tight truncate">
                {operator?.name || 'Operator'}
              </h1>
              {labels.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {labels.map((l, i) => (
                    <SupplierLabelBadge
                      key={`${l.key || l.label}-${i}`}
                      labelKey={l.key}
                      label={l.label}
                      tone={l.tone}
                    />
                  ))}
                </div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                {updated && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="text-[9px]"
                    />
                    Updated {updated}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* One button per intent — pitch and download no longer share a modal */}
          <div className="shrink-0 flex flex-wrap items-center gap-2">
            <CgRadixButton
              size="md"
              variant="secondary"
              onClick={() => setRequestOpen(true)}
              className="whitespace-nowrap"
            >
              <FontAwesomeIcon
                icon={faPaperPlane}
                className="text-xs"
              />
              Get suppliers CBAM-ready
            </CgRadixButton>
            <CgRadixButton
              size="md"
              variant="primary"
              onClick={() => setDownloadOpen(true)}
              className="whitespace-nowrap"
            >
              <FontAwesomeIcon
                icon={faDownload}
                className="text-xs"
              />
              Download data
            </CgRadixButton>
          </div>
        </div>
        {(operator?.profileText || operator?.websiteUrl || operator?.contactEmail) && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            {operator?.profileText && (
              <p className="text-sm text-slate-600 m-0 whitespace-pre-line">
                {operator.profileText}
              </p>
            )}
            {(operator?.websiteUrl || operator?.contactEmail) && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {operator.websiteUrl && (
                  <a
                    href={operator.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primaryColor no-underline hover:underline break-all"
                  >
                    {operator.websiteUrl}
                  </a>
                )}
                {operator.contactEmail && (
                  <a
                    href={`mailto:${operator.contactEmail}`}
                    className="text-primaryColor no-underline hover:underline"
                  >
                    {operator.contactEmail}
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <RequestSupplierDataModal
        open={requestOpen}
        onOpenChange={setRequestOpen}
      />

      <DownloadDataModal
        open={downloadOpen}
        onOpenChange={setDownloadOpen}
        operatorName={operator?.name}
        onDownloadExcel={onDownloadExcel}
        pdfAvailable={operator?.reportPdfAvailable === true}
        onDownloadPdf={reportPdf?.download}
        pdfPending={reportPdf?.isPending}
        pdfError={reportPdf?.error}
      />
    </div>
  );
}
